/**
 * Product sync job page (Phase 65 / BLU-594 PR3b) — /product-sync/jobs/:jobId.
 *
 * Server-state only: polls `getProductSyncJob` every 2s while active and stops
 * on a terminal state (mirrors the reconcile panel's honest-async rule). The
 * awaiting_approval state renders the approval gate, where an admin starts the
 * real Shopify/storage writes. A plan with nothing to review never waits there
 * (BLU-1522 S5): it starts on its own and the run says so (`stats.autoApproved`).
 * Cancel is a proper Dialog, never window.confirm.
 *
 * TWO queries, deliberately: the 2s poll carries stats only, and the plan (up
 * to 500 products, multi-MB) is fetched ONCE by `useProductSyncJobPlan`. Never
 * put the plan back on the poll.
 */
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle2, Loader2, PackageX, ShieldCheck, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PageErrorBoundary } from '@/components/fallbacks';
import { PageLoader } from '@/components/loading/PageLoader';
import { Skeleton } from '@/components/ui/skeleton';
import { usePageTitle } from '@/contexts/PageTitleContext';
import { useRole } from '@/hooks/use-role';
import { useOrganization } from '@/contexts/OrganizationContext';
import { trpc } from '@/lib/trpc';
import { JobStatusPill } from '@/features/product-sync/components/job/JobStatusPill';
import { JobTimeline } from '@/features/product-sync/components/job/JobTimeline';
import { JobApprovalGate } from '@/features/product-sync/components/job/JobApprovalGate';
import { JobRunningStats } from '@/features/product-sync/components/job/JobRunningStats';
import { CoverageStuckVariants } from '@/features/product-sync/components/job/CoverageStuckVariants';
import { CHECK_AGAIN_TOAST_KEYS } from '@/features/product-sync/lib/coverage-stuck';
import { useProductSyncJob, useProductSyncJobPlan } from '@/features/product-sync/hooks/useProductSyncJob';
import { asInventoryCoverage, asJobStats, asPlan, isActiveJobStatus } from '@/features/product-sync/lib/apply-types';
import { jobFailureKey } from '@/features/product-sync/lib/store-skus';

function ProductSyncJobPageInner() {
  const { t } = useTranslation('productSync');
  const { jobId = '' } = useParams();
  const { isAdmin } = useRole();
  const admin = isAdmin();
  const utils = trpc.useUtils();
  const jobQuery = useProductSyncJob(jobId || null);
  const job = jobQuery.data ?? null;
  // Fetched once, off the poll — see the file header.
  const planQuery = useProductSyncJobPlan(jobId || null, job?.status);
  const shortId = jobId.slice(0, 8);

  const { organization } = useOrganization();
  const [cancelOpen, setCancelOpen] = useState(false);
  // BLU-1519: run this job's inventory coverage now. The pass runs in the
  // background; the poll below shows its result.
  const checkAgainMutation = trpc.productSync.checkInventoryCoverageAgain.useMutation({
    onSuccess: ({ outcome }) => {
      toast.success(t(CHECK_AGAIN_TOAST_KEYS[outcome]));
      void utils.productSync.getProductSyncJob.invalidate({ jobId });
    },
    onError: (e) => toast.error(t('job.inventoryCoverage.checkFailed', { message: e.message }), { duration: Infinity }),
  });
  const cancelMutation = trpc.productSync.cancelProductSyncJob.useMutation({
    onSuccess: () => {
      toast.success(t('job.cancel.success'));
      setCancelOpen(false);
      void utils.productSync.getProductSyncJob.invalidate({ jobId });
      void utils.productSync.listProductSyncJobs.invalidate();
    },
    onError: (e) => {
      // Lost to the run starting (on its own, or another admin's approval):
      // say so plainly and show the run, never the server's wording.
      if (e.data?.code === 'BAD_REQUEST') {
        toast.error(t('job.cancel.alreadyStarted'));
        setCancelOpen(false);
        void utils.productSync.getProductSyncJob.invalidate({ jobId });
        return;
      }
      toast.error(t('job.cancel.error', { message: e.message }));
    },
  });

  usePageTitle(
    t('job.title', { id: shortId }),
    undefined,
    { label: t('page.title'), to: '/product-sync' },
  );

  // ---- loading / not-found / error --------------------------------------
  if (jobQuery.isLoading) {
    return <PageLoader />;
  }

  if (jobQuery.isError || !job) {
    const notFound = jobQuery.error?.data?.code === 'NOT_FOUND' || !job;
    return (
      <div className="container max-w-4xl px-6 py-6">
        <div className="flex flex-col items-center gap-3 rounded-lg border py-16 text-center">
          <div className="grid h-11 w-11 place-items-center rounded-xl bg-muted text-muted-foreground">
            <PackageX className="h-5 w-5" aria-hidden="true" />
          </div>
          <p className="text-sm font-semibold">{notFound ? t('job.notFound.title') : t('states.errorTitle')}</p>
          <p className="mx-auto max-w-sm text-sm text-muted-foreground">
            {notFound ? t('job.notFound.body') : (jobQuery.error?.message ?? t('states.errorBody'))}
          </p>
          <Button asChild variant="outline" size="sm">
            <Link to="/product-sync">{t('job.notFound.back')}</Link>
          </Button>
        </div>
      </div>
    );
  }

  const plan = asPlan(planQuery.data?.plan);
  const stats = asJobStats(job.stats);
  const inventoryCoverage = asInventoryCoverage(stats.inventoryCoverage);
  const terminalInventoryCoverage = inventoryCoverage?.status === 'pending'
    && inventoryCoverage.autoRetry === false;
  const canCancel = admin && (job.status === 'queued' || job.status === 'planning' || job.status === 'awaiting_approval');
  // 0 = not known yet (plan in flight, or nulled on a finished job) — JobRunningStats treats that as indeterminate, never "of 0".
  const totalProducts = plan?.products.length ?? 0;
  // Phase 88: a store SKU set-up job, from its plan or (once the plan is gone) its stats.
  const declaredJob = plan?.identity === 'declared' || typeof stats.declared === 'number';
  // A known failure literal reads as a plain sentence; free text shows as it is.
  const failureKey = jobFailureKey(job.error);

  return (
    <div className="container max-w-4xl px-6 py-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3" role="status" aria-live="polite">
          <JobStatusPill status={job.status} size="lg" />
          {isActiveJobStatus(job.status) && job.status !== 'awaiting_approval' && (
            <span className="text-xs text-muted-foreground">{t('job.polling')}</span>
          )}
        </div>
        {canCancel && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCancelOpen(true)}
            className="h-8 gap-1.5 border-orange-300 px-3 text-xs text-orange-600 hover:bg-orange-50 hover:text-orange-700"
          >
            {t('job.cancel.button')}
          </Button>
        )}
      </div>

      <div className="mt-5 grid gap-6 lg:grid-cols-[1fr_15rem]">
        {/* Main column */}
        <div className="min-w-0 space-y-4 lg:order-1">
          {job.status === 'failed' && (
            <div className="flex items-start gap-2.5 rounded-lg border border-red-200 bg-red-50/60 px-4 py-3">
              <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" aria-hidden="true" />
              <div>
                <p className="text-sm font-semibold text-red-800">{t('job.failed.title')}</p>
                {job.error && <p className="mt-0.5 text-xs text-red-700">{failureKey ? t(failureKey) : job.error}</p>}
              </div>
            </div>
          )}

          {job.status === 'cancelled' && (
            <div className="flex items-center gap-2.5 rounded-lg border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
              <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
              {t('job.cancelled.note')}
            </div>
          )}

          {job.status === 'completed' && inventoryCoverage?.status !== 'pending' && (
            <div className="flex items-center gap-2.5 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
              <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />
              {declaredJob ? t('job.completed.declaredNote') : t('job.completed.note')}
            </div>
          )}

          {job.status === 'completed' && inventoryCoverage?.status === 'pending' && inventoryCoverage.stuckVariants && (
            <CoverageStuckVariants
              coverage={inventoryCoverage}
              shopifyDomain={organization?.shopify_domain}
              admin={admin}
              checking={checkAgainMutation.isPending}
              onCheckAgain={() => checkAgainMutation.mutate({ jobId })}
            />
          )}

          {job.status === 'completed' && inventoryCoverage?.status === 'pending' && !inventoryCoverage.stuckVariants && (
            <div
              className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50/70 px-4 py-3 text-amber-900"
              role="status"
              aria-live="polite"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
              <div>
                <p className="text-sm font-semibold">
                  {t(terminalInventoryCoverage
                    ? 'job.inventoryCoverage.terminalTitle'
                    : 'job.inventoryCoverage.pendingTitle')}
                </p>
                <p className="mt-0.5 text-xs text-amber-800">
                  {terminalInventoryCoverage
                    ? inventoryCoverage.pendingBlankSkus !== undefined
                      ? t('job.inventoryCoverage.terminalBody', { count: inventoryCoverage.pendingBlankSkus })
                      : t('job.inventoryCoverage.terminalBodyUnknown')
                    : inventoryCoverage.pendingBlankSkus !== undefined
                      ? t('job.inventoryCoverage.pendingBody', { count: inventoryCoverage.pendingBlankSkus })
                      : t('job.inventoryCoverage.pendingBodyUnknown')}
                </p>
              </div>
            </div>
          )}

          {(job.status === 'queued' || job.status === 'planning') && (
            <div className="flex flex-col items-center gap-3 rounded-lg border py-14 text-center">
              <Loader2 className="h-6 w-6 animate-spin text-threadheads-purple" aria-hidden="true" />
              <div className="space-y-1">
                <p className="text-sm font-semibold">{t('job.planning.title')}</p>
                <p className="mx-auto max-w-sm text-sm text-muted-foreground">{t('job.planning.body')}</p>
              </div>
            </div>
          )}

          {job.status === 'awaiting_approval' &&
            (plan ? (
              <JobApprovalGate jobId={jobId} plan={plan} admin={admin} onRequestCancel={() => setCancelOpen(true)} />
            ) : planQuery.isError ? (
              <div className="flex items-start gap-2.5 rounded-lg border border-red-200 bg-red-50/60 px-4 py-3">
                <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" aria-hidden="true" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-red-800">{t('job.plan.errorTitle')}</p>
                  <p className="mt-0.5 text-xs text-red-700">{t('job.plan.errorBody')}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2 h-8 gap-1.5 px-3 text-xs"
                    onClick={() => void planQuery.refetch()}
                    disabled={planQuery.isFetching}
                  >
                    {planQuery.isFetching ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : null}
                    {t('states.retry')}
                  </Button>
                </div>
              </div>
            ) : (
              /* Plan in flight: skeletons that match the gate's card grid, so arrival doesn't shift the page. */
              <div className="space-y-3" aria-busy="true">
                <p className="text-sm text-muted-foreground" aria-live="polite">{t('job.plan.loading')}</p>
                <div className="grid gap-3 lg:grid-cols-2">
                  <Skeleton className="h-32 w-full rounded-lg" />
                  <Skeleton className="h-32 w-full rounded-lg" />
                  <Skeleton className="h-32 w-full rounded-lg" />
                  <Skeleton className="h-32 w-full rounded-lg" />
                </div>
              </div>
            ))}

          {stats.autoApproved === true && (
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="auto-approved">
              <ShieldCheck className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              {t('job.autoApproved')}
            </p>
          )}

          {(job.status === 'running' || job.status === 'completed' || job.status === 'failed' || job.status === 'cancelled') && (
            <JobRunningStats
              stats={stats}
              totalProducts={totalProducts}
              status={job.status}
              declared={declaredJob}
              finishedAt={job.finished_at}
            />
          )}
        </div>

        {/* Timeline sidebar */}
        <aside className="lg:order-2">
          <div className="rounded-lg border bg-background p-4">
            <p className="mb-3 text-[11px] font-bold uppercase tracking-wide text-muted-foreground/70">{t('job.timeline.title')}</p>
            <JobTimeline createdAt={job.created_at} startedAt={job.started_at} finishedAt={job.finished_at} />
          </div>
        </aside>
      </div>

      {/* Cancel dialog */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('job.cancel.dialogTitle')}</DialogTitle>
            <DialogDescription>{t('job.cancel.dialogBody')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCancelOpen(false)} disabled={cancelMutation.isPending}>
              {t('job.cancel.keep')}
            </Button>
            <Button
              onClick={() => cancelMutation.mutate({ jobId })}
              disabled={cancelMutation.isPending}
              className="gap-1.5 border-orange-300 bg-orange-600 text-white hover:bg-orange-700"
            >
              {cancelMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
              {t('job.cancel.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function ProductSyncJobPage() {
  return (
    <PageErrorBoundary>
      <ProductSyncJobPageInner />
    </PageErrorBoundary>
  );
}
